import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Chip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Avatar,
  IconButton,
  Badge,
  Popover,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import axios from "axios";
import {
  resolveAssignedUserRoleGroups,
} from "./assignedUserRoleResolution";

const ASSIGN_USER_MENU_MAX_HEIGHT = 320;
const ASSIGNED_USERS_LIST_MAX_HEIGHT = 150;
const GROUPED_ASSIGNED_USER_AVATAR_SIZE = 50;

function OrderUserAssignment({
  orderId,
  initialAssignedUsers,
  onChangeAssignedUsers,
  onSave,                 // ✅ NEW (optional)
  saveDebounceMs = 350,   // ✅ NEW (optional)
}) {
  // assignedUsers is stored as an array of objects with { id, role, ... }
  const [assignedUsers, setAssignedUsers] = useState(initialAssignedUsers || []);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [assignedUsersView, setAssignedUsersView] = useState("default");
  const [assignedUsersAnchorEl, setAssignedUsersAnchorEl] = useState(null);
  const { instanceUniqueId } = useParams();

  // Utility function to extract the correct profile picture URL
  const getUserAvatar = (user) => {
    const defaultAvatarUrl = "/elite-core-placeholder-avatar.png";
    if (!user || !user.profilePicture) return defaultAvatarUrl;

    let profilePic = user.profilePicture;

    try {
      if (typeof profilePic === "string") profilePic = JSON.parse(profilePic);
    } catch (e) {
      profilePic = user.profilePicture;
    }

    if (Array.isArray(profilePic) && profilePic.length > 0) {
      const selectedPic = profilePic.find((pic) => pic.selected) || profilePic[0];
      if (selectedPic && selectedPic.url) {
        if (selectedPic.url.startsWith("uploads")) {
          return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${selectedPic.url}`;
        }
        return selectedPic.url;
      }
    }

    if (typeof profilePic === "string") {
      if (profilePic.startsWith("uploads")) {
        return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${profilePic}`;
      }
      return profilePic;
    }

    return defaultAvatarUrl;
  };

  // Load available users for the current instance
  useEffect(() => {
    async function fetchUsers() {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstanceUniqueId/${instanceUniqueId}?archiveStatus=all`
        );
        setAvailableUsers(response.data.listOfUsers || []);
      } catch (error) {
        alert("Error fetching users");
        console.error("Error fetching users", error);
      }
    }
    if (instanceUniqueId) fetchUsers();
  }, [instanceUniqueId]);

  useEffect(() => {
    async function fetchAssignedUsersView() {
      try {
        const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
        const instanceResponse = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        const instanceId = instanceResponse.data?.id;
        if (!instanceId) return;

        const defaultsResponse = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/defaults`,
          headers
        );
        setAssignedUsersView(
          defaultsResponse.data?.defaults?.assignedUsersView === "grouped" ? "grouped" : "default"
        );
      } catch (error) {
        console.error("Error fetching assigned users view", error);
        setAssignedUsersView("default");
      }
    }

    if (instanceUniqueId) fetchAssignedUsersView();
  }, [instanceUniqueId]);

  // Keep assignedUsers in sync if parent changes the initial list
  useEffect(() => {
    setAssignedUsers(initialAssignedUsers || []);
  }, [initialAssignedUsers]);

  // Notify parent when assignedUsers changes
  useEffect(() => {
    if (typeof onChangeAssignedUsers === "function") {
      onChangeAssignedUsers(assignedUsers);
    }
  }, [assignedUsers, onChangeAssignedUsers]);

  // ✅ NEW: call onSave when assignedUsers changes (debounced)
  const saveTimerRef = useRef(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    if (typeof onSave !== "function") return;

    // skip initial mount (don’t auto-save existing assignments)
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      onSave(assignedUsers);
    }, saveDebounceMs);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [assignedUsers, onSave, saveDebounceMs]);

  // Helper to lookup a user in availableUsers by id.
  const getUserDetail = (id) => {
    return availableUsers.find((user) => user.id === id) || {};
  };

  const handleAssignUser = (userId) => {
    if (!userId) return;

    const selectedUserObj = availableUsers.find((user) => user.id === userId);
    if (!selectedUserObj) {
      alert("User not found in the available users.");
      return;
    }
    const selectedArchiveStatus = String(selectedUserObj?.archiveStatus || "").trim().toLowerCase();
    if (selectedUserObj?.archived === true || ["archiving", "archived"].includes(selectedArchiveStatus)) {
      alert("Archived users cannot be assigned.");
      setSelectedUser("");
      return;
    }

    const roleFromUser = selectedUserObj.role || "customer";

    const existingUser = assignedUsers.find((u) => Number(u.id) === Number(userId));
    if (existingUser) {
      const updatedUser = {
        ...existingUser,
        role: roleFromUser,
        first_name: selectedUserObj.first_name,
        last_name: selectedUserObj.last_name,
        profilePicture: selectedUserObj.profilePicture,
      };
      setAssignedUsers(assignedUsers.map((u) => (Number(u.id) === Number(userId) ? updatedUser : u)));
      setSelectedUser("");
      return;
    }

    const newAssignedUser = {
      id: userId,
      role: roleFromUser,
      first_name: selectedUserObj.first_name,
      last_name: selectedUserObj.last_name,
      profilePicture: selectedUserObj.profilePicture,
    };

    setAssignedUsers([...assignedUsers, newAssignedUser]);
    setSelectedUser("");
  };

  const handleRemoveUser = (userId) => {
    setAssignedUsers(assignedUsers.filter((user) => Number(user.id) !== Number(userId)));
  };

  const getUserOptionLabel = (user) => {
    const displayName = user.first_name ? `${user.first_name} ${user.last_name}` : user.name;
    return `${displayName || "Unknown"} (${user.role || "customer"})`;
  };

  const getAssignedUserDisplay = (assigned) => {
    const userDetail = getUserDetail(assigned.id);
    const user = { ...assigned, ...userDetail };
    const displayName = user.first_name
      ? `${user.first_name} ${user.last_name || ""}`.trim()
      : user.name || "Unknown";
    return { user, displayName };
  };

  const ArchivedIndicator = ({ user, size = 18 }) => {
    if (user?.archived !== true) return null;

    return (
      <Tooltip title="This user is archived for this Elite Core instance. Historical assignment is being retained.">
        <span
          aria-label="Archived user"
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            width: size,
            height: size,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--ec-color-surface, #fff)",
            boxShadow: "0 1px 4px rgba(15, 23, 42, 0.18)",
            zIndex: 2,
          }}
        >
          <ArchiveRoundedIcon sx={{ fontSize: Math.max(12, size - 5), color: "text.secondary" }} />
        </span>
      </Tooltip>
    );
  };

  const resolvedRoleGroups = resolveAssignedUserRoleGroups(assignedUsers);
  const hasGroupedUsers = resolvedRoleGroups.some((group) => group.users.length || group.note);
  const showAssignedUsersPopoverButton = assignedUsersView === "grouped" && assignedUsers.length > 0;
  const assignedUsersPopoverOpen = showAssignedUsersPopoverButton && Boolean(assignedUsersAnchorEl);

  return (
    <div
      className="assigned-users-section"
      style={{
        position: "relative",
        ...(showAssignedUsersPopoverButton ? { paddingRight: 64 } : {}),
      }}
    >
      {showAssignedUsersPopoverButton ? (
        <>
          <Tooltip title="View all assigned users">
            <IconButton
              aria-label={`View all ${assignedUsers.length} assigned users`}
              size="small"
              onClick={(event) => setAssignedUsersAnchorEl(event.currentTarget)}
              sx={{
                position: "absolute",
                top: 15,
                right: 15,
                color: "text.secondary",
              }}
            >
              <Badge badgeContent={assignedUsers.length} color="primary">
                <GroupsRoundedIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Popover
            open={assignedUsersPopoverOpen}
            anchorEl={assignedUsersAnchorEl}
            onClose={() => setAssignedUsersAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 0.75,
                width: 280,
                maxWidth: "calc(100vw - 32px)",
                p: 1.25,
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              All Assigned Users
            </Typography>
            <div style={{ display: "grid", gap: 8, maxHeight: 320, overflowY: "auto" }}>
              {assignedUsers.map((assigned) => {
                const { user, displayName } = getAssignedUserDisplay(assigned);
                return (
                  <div
                    key={`${assigned.id}-${assigned.role || "user"}`}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
                      <Avatar src={getUserAvatar(user)} sx={{ width: 32, height: 32 }} />
                      <ArchivedIndicator user={user} size={16} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.25 }} noWrap>
                        {displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {assigned.role || "customer"}
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </div>
          </Popover>
        </>
      ) : null}
      <Typography variant="h6" gutterBottom>
        Assigned Users
      </Typography>

      {assignedUsersView === "grouped" ? (
        hasGroupedUsers ? (
          <div style={{ display: "grid", gap: 12 }}>
            {resolvedRoleGroups.map(({ role, label, users, note }) => {
              if (users.length === 0 && !note) return null;

              return (
                <div key={role}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {label}
                  </Typography>
                  {note ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
                    >
                      {note}
                    </Typography>
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 4,
                      minWidth: 0,
                    }}
                  >
                    {users.map((assigned) => {
                      const { user, displayName } = getAssignedUserDisplay(assigned);

                      return (
                        <div
                          key={assigned.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minWidth: 140,
                            maxWidth: "100%",
                          }}
                        >
                          <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
                            <Avatar
                              src={getUserAvatar(user)}
                              sx={{
                                width: GROUPED_ASSIGNED_USER_AVATAR_SIZE,
                                height: GROUPED_ASSIGNED_USER_AVATAR_SIZE,
                              }}
                            />
                            <ArchivedIndicator user={user} />
                          </span>
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {displayName}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Typography variant="body1">No users added yet.</Typography>
        )
      ) : assignedUsers.length === 0 ? (
        <Typography variant="body1">No users added yet.</Typography>
      ) : (
        <div
          className="user-option"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            maxHeight: ASSIGNED_USERS_LIST_MAX_HEIGHT,
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 4,
          }}
        >
          {assignedUsers.map((assigned) => {
            const { user, displayName } = getAssignedUserDisplay(assigned);
            const tooltipText = `${displayName} (${assigned.role})`;

            return (
              <div
                key={assigned.id}
                style={{
                  position: "relative",
                  width: "calc(50% - 4px)",
                  maxWidth: "100%",
                  minWidth: 0,
                }}
              >
                <Tooltip title={tooltipText}>
                  <Chip
                    avatar={<Avatar src={getUserAvatar(user)} />}
                    label={`${displayName} (${assigned.role})`}
                    onDelete={() => handleRemoveUser(assigned.id)}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      "& .MuiChip-label": {
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "@media (max-width: 600px)": {
                        width: "100% !important",
                      },
                    }}
                  />
                </Tooltip>
                <ArchivedIndicator user={user} />
              </div>
            );
          })}
        </div>
      )}

      <FormControl fullWidth style={{ marginTop: "1rem" }}>
        <InputLabel id="assign-user-label">Assign User</InputLabel>
        <Select
          labelId="assign-user-label"
          value={selectedUser}
          label="Assign User"
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: ASSIGN_USER_MENU_MAX_HEIGHT,
                overflowY: "auto",
              },
            },
            MenuListProps: {
              style: {
                maxHeight: ASSIGN_USER_MENU_MAX_HEIGHT,
                overflowY: "auto",
              },
            },
          }}
          onChange={(e) => {
            const userId = e.target.value;
            setSelectedUser(userId);
            handleAssignUser(userId);
          }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>

          {availableUsers.filter((user) => {
            const status = String(user?.archiveStatus || "").trim().toLowerCase();
            return user?.archived !== true && !["archiving", "archived"].includes(status);
          }).map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {getUserOptionLabel(user)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

export default OrderUserAssignment;
